import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { initDeeplink, onDeepLink } from '../services/deeplink';
import GitHubIcon from '../components/GitHubIcon';
import {
  loginUser,
  registerUser,
  checkCode,
  verifyEmail,
  forgotPassword,
  resetPassword,
  resendCode,
} from '../services/api';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState(null);
  const isNative = Capacitor.isNativePlatform();

  // ── Form state ────────────────────────────────────────────────

  const [mode, setMode] = useState('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');

  // True once the entered code has been validated by the backend
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);

  // Track last auto-checked code to avoid re-checking the same value
  const lastChecked = useRef('');

  // ── Determine initial mode from URL params ────────────────────

  const urlVerify = searchParams.get('verify') || '';
  const urlReset = searchParams.get('reset') || '';

  useEffect(() => {
    if (urlVerify) {
      setMode('verify');
      setCode(urlVerify);
      return;
    }
    if (urlReset) {
      setMode('reset');
      setCode(urlReset);
      return;
    }
  }, [urlVerify, urlReset]);

  // ── Auto-check code when 6 digits filled ──────────────────────

  const autoCheck = useCallback(async (value) => {
    if (value.length !== 6) {
      setCodeVerified(false);
      return;
    }
    if (value === lastChecked.current) return;

    lastChecked.current = value;
    setCodeChecking(true);
    setError('');
    try {
      const result = await checkCode(value);
      if (result.valid) {
        setCodeVerified(true);
      } else {
        setCodeVerified(false);
        setError('Invalid or expired code');
      }
    } catch {
      setCodeVerified(false);
      setError('Failed to verify code — please try again');
    } finally {
      setCodeChecking(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'verify' || mode === 'reset') {
      autoCheck(code);
    } else {
      setCodeVerified(false);
      setCodeChecking(false);
      lastChecked.current = '';
    }
  }, [code, mode, autoCheck]);

  // ── OAuth callback — URL params (web) or deeplink (native) ────

  useEffect(() => {
    if (urlVerify || urlReset) return;

    const githubToken = searchParams.get('github_token');
    const googleToken = searchParams.get('google_token');
    const token = githubToken || googleToken;
    const user = searchParams.get('username');
    const uid = searchParams.get('user_id');
    const refreshToken = searchParams.get('refresh_token');

    if (token && user) {
      const provider = githubToken ? 'github' : 'google';
      handleOAuthSuccess(token, user, uid, provider, refreshToken);
      return;
    }

    if (isNative) {
      initDeeplink();

      const handleCallback = async (url) => {
        const params = new URLSearchParams(url.split('?')[1] || '');
        const cbToken = params.get('github_token') || params.get('google_token');
        if (cbToken && params.get('username')) {
          const cbProvider = params.get('github_token') ? 'github' : 'google';
          try { await Browser.close(); } catch { /* ignore */ }
          handleOAuthSuccess(cbToken, params.get('username'),
            params.get('user_id'), cbProvider, params.get('refresh_token'));
        }
      };

      const unsubGithub = onDeepLink('github_token', handleCallback);
      const unsubGoogle = onDeepLink('google_token', handleCallback);

      return () => { unsubGithub(); unsubGoogle(); };
    }
  }, [searchParams, navigate, isNative]);

  const handleOAuthSuccess = (token, user, uid, provider, refreshToken) => {
    localStorage.setItem('access_token', token);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('username', user);
    localStorage.setItem('login_provider', provider);
    if (uid) localStorage.setItem('user_id', uid);
    navigate('/', { replace: true });
  };

  // ── Firebase Google Sign-In (native only) ────────────────────

  const exchangeFirebaseToken = async (firebaseIdToken) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://julynote.app';
    const url = `${baseUrl}/api/user/firebase-login`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: firebaseIdToken }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Authentication failed');
    }
    const data = await response.json();
    localStorage.setItem('access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('username', data.username);
    navigate('/', { replace: true });
  };

  const handleGoogleLogin = async () => {
    setError(''); setIsLoading(true); setLoadingProvider('google');
    try {
      const { signInWithGoogle } = await import('../capacitorFirebase');
      await exchangeFirebaseToken(await signInWithGoogle());
    } catch (err) {
      if (err?.message?.includes('cancel')) { setIsLoading(false); setLoadingProvider(null); return; }
      setError('Sign in failed — please try again.');
    } finally { setIsLoading(false); setLoadingProvider(null); }
  };

  const handleAppleLogin = async () => {
    setError(''); setIsLoading(true); setLoadingProvider('apple');
    try {
      const { signInWithApple } = await import('../capacitorFirebase');
      await exchangeFirebaseToken(await signInWithApple());
    } catch (err) {
      if (err?.message?.includes('cancel')) { setIsLoading(false); setLoadingProvider(null); return; }
      setError('Sign in failed — please try again.');
    } finally { setIsLoading(false); setLoadingProvider(null); }
  };

  // ── Git provider OAuth ───────────────────────────────────────

  const openOAuth = async (provider) => {
    setError(''); setIsLoading(true); setLoadingProvider(provider);
    const authPaths = { github: '/api/auth/github/login', google: '/api/auth/google/login' };
    const authPath = authPaths[provider] || `/api/auth/${provider}/login`;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://julynote.app';
    const authUrl = `${isNative ? baseUrl : ''}${authPath}?origin=${encodeURIComponent(isNative ? 'julynote://' : window.location.origin)}`;
    if (isNative) {
      await Browser.open({ url: authUrl, windowName: '_blank' });
      // Loading state stays active until the deeplink callback resolves it
    } else {
      window.location.href = authUrl;
    }
  };

  // ── Form handlers ────────────────────────────────────────────

  const clearMessages = () => { setError(''); setSuccess(''); };

  const handleResend = async () => {
    if (!email.trim()) return;
    setError('');
    setCodeChecking(true);
    lastChecked.current = '';
    setCodeVerified(false);
    try {
      const codeType = mode === 'verify' ? 'verify' : 'reset';
      await resendCode(email.trim(), codeType);
      setSuccess('New code sent — check your email');
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setCodeChecking(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setPassword('');
    setConfirmPassword('');
    setCode('');
    setCodeVerified(false);
    lastChecked.current = '';
    if (newMode === 'login' || newMode === 'register' || newMode === 'forgot') {
      setEmail('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);

    try {
      if (mode === 'login') {
        if (!email.trim() || !password) throw new Error('Please enter your email and password');
        try {
          await loginUser(email.trim(), password);
        } catch {
          throw new Error('Sign in failed — please check your credentials.');
        }
        navigate('/', { replace: true });

      } else if (mode === 'register') {
        if (!email.trim()) throw new Error('Please enter your email address');
        await registerUser(email.trim());
        setSuccess('Code sent — check your email');
        setMode('verify');

      } else if (mode === 'forgot') {
        if (!email.trim()) throw new Error('Please enter your email address');
        await forgotPassword(email.trim());
        setSuccess('Code sent — check your email');
        setMode('reset');

      } else if (mode === 'verify') {
        if (!codeVerified) throw new Error('Please enter a valid verification code');
        if (!password || password.length < 8) throw new Error('Password must be at least 8 characters');
        if (password !== confirmPassword) throw new Error('Passwords do not match');
        await verifyEmail(code.trim(), password);
        setSuccess('Email verified! You can now sign in.');
        switchMode('login');

      } else if (mode === 'reset') {
        if (!codeVerified) throw new Error('Please enter a valid reset code');
        if (!password || password.length < 8) throw new Error('Password must be at least 8 characters');
        if (password !== confirmPassword) throw new Error('Passwords do not match');
        await resetPassword(code.trim(), password);
        setSuccess('Password reset! You can now sign in.');
        switchMode('login');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Taglines & button labels ─────────────────────────────────

  const taglines = {
    login: 'A calm space for your thoughts',
    register: 'Create your account',
    verify: codeVerified ? 'Set your password' : 'Check your email for a code',
    forgot: 'Reset your password',
    reset: codeVerified ? 'Set a new password' : 'Check your email for a code',
  };

  const buttons = {
    login: 'Sign in',
    register: 'Send verification code',
    verify: codeVerified ? 'Set password & sign in' : 'Checking code...',
    forgot: 'Send reset code',
    reset: codeVerified ? 'Set new password' : 'Checking code...',
  };

  // ── Render ───────────────────────────────────────────────────

  const showOAuth = mode === 'login' || mode === 'register';

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">JulyNote</div>
        <p className="login-tagline">{taglines[mode] || ''}</p>

        {error && <div className="login-error">{error}</div>}
        {success && <div className="login-success">{success}</div>}

        <form className="login-email-form" onSubmit={handleSubmit}>
          {/* Email — login, register, forgot */}
          {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
            <div className="form-group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="Email"
                disabled={isLoading}
              />
            </div>
          )}

          {/* Email hint — verify, reset */}
          {(mode === 'verify' || mode === 'reset') && email && (
            <p className="login-code-hint">
              Code sent to <strong>{email}</strong>
            </p>
          )}

          {/* Verification code — verify, reset */}
          {(mode === 'verify' || mode === 'reset') && (
            <div className="form-group">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoComplete="one-time-code"
                placeholder="000000"
                className={`login-code-input${codeVerified ? ' code-valid' : ''}${codeChecking ? ' code-checking' : ''}`}
                disabled={isLoading}
              />
              {codeChecking && <span className="login-code-status">Checking...</span>}
              {codeVerified && <span className="login-code-status code-ok">✓ Valid</span>}
              {!codeVerified && !codeChecking && (
                <button type="button" className="login-link login-resend" onClick={handleResend}>
                  Resend code
                </button>
              )}
            </div>
          )}

          {/* Password — login */}
          {mode === 'login' && (
            <div className="form-group">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Password"
                disabled={isLoading}
              />
            </div>
          )}

          {/* Password + confirm — verify, reset (only after code validated) */}
          {(mode === 'verify' || mode === 'reset') && codeVerified && (
            <>
              <div className="form-group">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="New password (min 8 characters)"
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="Confirm password"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          <button
            type="submit"
            className="login-submit-btn"
            disabled={isLoading || ((mode === 'verify' || mode === 'reset') && !codeVerified)}
          >
            {isLoading ? 'Please wait...'
              : (mode === 'verify' || mode === 'reset') && codeChecking ? 'Checking code...'
              : buttons[mode] || 'Submit'}
          </button>
        </form>

        {/* Mode toggle links */}
        <div className="login-form-links">
          {mode === 'login' && (
            <>
              <button type="button" className="login-link" onClick={() => { clearMessages(); switchMode('forgot'); }}>
                Forgot password?
              </button>
              <span className="login-link-sep">·</span>
              <button type="button" className="login-link" onClick={() => { clearMessages(); switchMode('register'); }}>
                Create account
              </button>
            </>
          )}
          {mode !== 'login' && (
            <button type="button" className="login-link" onClick={() => { clearMessages(); switchMode('login'); }}>
              ← Back to sign in
            </button>
          )}
        </div>

        {/* OAuth */}
        {showOAuth && (
          <>
            <div className="login-divider">or continue with</div>
            <div className="login-providers">
              {isNative && (
                <button type="button" className="login-provider-btn apple-button"
                  onClick={handleAppleLogin} disabled={isLoading}>
                  <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                    <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  {loadingProvider === 'apple' ? 'Signing in...' : 'Apple'}
                </button>
              )}
              {isNative ? (
                <button type="button" className="login-provider-btn google-button"
                  onClick={handleGoogleLogin} disabled={isLoading}>
                  <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {loadingProvider === 'google' ? 'Signing in...' : 'Google'}
                </button>
              ) : (
                <button type="button" className="login-provider-btn google-button"
                  onClick={() => openOAuth('google')} disabled={isLoading}>
                  <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </button>
              )}
              <button type="button" className="login-provider-btn" onClick={() => openOAuth('github')} disabled={isLoading}>
                <GitHubIcon className="provider-icon" size={20} />
                {loadingProvider === 'github' ? 'Signing in...' : 'GitHub'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
